import { Directive, ElementRef, HostAttributeToken, inject, type OnDestroy } from '@angular/core';

@Directive({
  selector: '[appTruncateFilename]'
})
export class TruncateFilenameDirective implements OnDestroy {
  readonly #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #ellipsis = inject(new HostAttributeToken('ellipsisClassName'), { optional: true });

  readonly #observer: ResizeObserver;
  readonly #ellipsisClassName: string;
  #originalText = '';

  constructor() {
    const ellipsis = this.#ellipsis;

    this.#ellipsisClassName = ellipsis || 'text-truncate';
    this.#observer = new ResizeObserver(this.#observerCallback.bind(this));
    this.#observer.observe(this.#elementRef.nativeElement);
  }

  public ngOnDestroy(): void {
    this.#observer.disconnect();
  }

  #observerCallback(entries: readonly ResizeObserverEntry[]): void {
    if (!this.#originalText) {
      this.#originalText = this.#elementRef.nativeElement.textContent || '';
    }

    const text = this.#originalText;
    const extension = text.substring(text.lastIndexOf('.'));

    if (!extension) {
      // No file extension. Use css ellipsis
      this.#elementRef.nativeElement.classList.add(this.#ellipsisClassName);
      this.#observer.disconnect();
      return;
    }

    const font = getComputedStyle(this.#elementRef.nativeElement).font;
    const containerWidth = entries[0].contentRect.width;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      // Error creating context. Fall back to css ellipsis
      this.#elementRef.nativeElement.classList.add(this.#ellipsisClassName);
      return;
    }

    context.font = font;
    const textWidth = context.measureText(text).width;

    if (textWidth <= containerWidth) {
      // No text overflow. Restore original text if it has been truncated before
      this.#elementRef.nativeElement.innerHTML = this.#originalText;
      return;
    }

    const name = text.substring(0, text.lastIndexOf('.'));
    const nameWidth = context.measureText(name).width;
    const extensionWidth = context.measureText(`... ${extension}`).width;
    const targetWidth = containerWidth - extensionWidth;
    let targetLength = Math.floor((targetWidth * name.length) / nameWidth);

    if (targetLength <= 1) {
      this.#elementRef.nativeElement.innerHTML = `${text[0]}... ${extension}`;
      return;
    }

    targetLength = this.#tuneLength(context, targetWidth, targetLength);
    this.#elementRef.nativeElement.innerHTML = `${text.substring(0, targetLength)}... ${extension}`;
  }

  #tuneLength(context: CanvasRenderingContext2D, targetWidth: number, targetLength: number): number {
    let text = this.#originalText.substring(0, targetLength);
    let width = context.measureText(text).width;
    let length = targetLength;
    if (width < targetWidth) {
      while (width <= targetWidth) {
        length++;
        text = this.#originalText.substring(0, length);
        width = context.measureText(text).width;
      }
      return length - 1;
    } else {
      while (width > targetWidth) {
        length--;
        text = this.#originalText.substring(0, length);
        width = context.measureText(text).width;
      }
      return length;
    }
  }
}
